import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { experience } from '../data/experience';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { profile } from '../data/profile';

const contacts = [
  { label: 'Email', value: profile.email, href: `mailto:${profile.email}`, Icon: MdEmail },
  { label: 'LinkedIn', value: '/kyle-d-speer', href: profile.linkedin, Icon: FaLinkedin },
  { label: 'GitHub', value: '/kspeer825', href: profile.github, Icon: FaGithub },
];

// The role crossing this line (as a fraction of the viewport height) is in focus
const FOCUS_LINE = 0.45;

// Distance from the focused role (in roles) -> how enlarged and how visible a role is
const scaleAt = (d: number) => 1 + 0.4 * Math.exp(-(d * d) / 1.2);
const opacityAt = (d: number) => Math.max(0.15, 1 - 0.35 * d);

const canHover = () => window.matchMedia('(hover: hover)').matches;

// Where the focus line sits in the viewport. It never sits below the newest role's
// position at the top of the page, so the newest role is in focus before any scrolling.
const focusLineFor = (firstRow: HTMLElement) => {
  const rect = firstRow.getBoundingClientRect();
  const firstCenter = window.scrollY + rect.top + rect.height / 2;
  return Math.min(window.innerHeight * FOCUS_LINE, firstCenter);
};

function Professional() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('role');
  const rowRefs = useRef<(HTMLDivElement | null)[]>([]);
  const anchor = useRef<{ index: number; top: number } | null>(null);
  const [scrollFocus, setScrollFocus] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);

  // Track which role is crossing the focus line as the page scrolls. The value is
  // fractional between two roles so the enlarge and fade move smoothly.
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      const centers = rowRefs.current.map((row) => {
        const rect = row!.getBoundingClientRect();
        return rect.top + rect.height / 2;
      });
      // On screens too tall for the oldest role to scroll up to the line, drift the line
      // down as the page scrolls so it reaches the oldest role exactly at the bottom
      let line = focusLineFor(rowRefs.current[0]!);
      const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
      if (maxScroll > 0) {
        const lastCenter = window.scrollY + centers[centers.length - 1];
        const shortfall = lastCenter - (maxScroll + line);
        if (shortfall > 0) line += (shortfall * window.scrollY) / maxScroll;
      }
      let focus = centers.length - 1;
      for (let i = 0; i < centers.length - 1; i++) {
        if (line < centers[i + 1]) {
          focus = line <= centers[i] ? i : i + (line - centers[i]) / (centers[i + 1] - centers[i]);
          break;
        }
      }
      setScrollFocus(focus);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
  }, [selectedId]);

  // Arriving (or refreshing) starts with the newest role in focus, unless a shared
  // ?role= link asks for a specific role, which is brought to the focus line instead
  useEffect(() => {
    const row = rowRefs.current[experience.findIndex((role) => role.id === selectedId)];
    requestAnimationFrame(() => {
      if (!row) {
        window.scrollTo({ top: 0 });
        return;
      }
      const rect = row.getBoundingClientRect();
      const line = focusLineFor(rowRefs.current[0]!);
      window.scrollTo({ top: window.scrollY + rect.top + rect.height / 2 - line });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the clicked role where it was on screen when a role above it collapses
  useLayoutEffect(() => {
    const row = anchor.current && rowRefs.current[anchor.current.index];
    if (row) window.scrollBy(0, row.getBoundingClientRect().top - anchor.current!.top);
    anchor.current = null;
  }, [selectedId]);

  const toggle = (id: string, index: number) => {
    anchor.current = { index, top: rowRefs.current[index]!.getBoundingClientRect().top };
    setSearchParams(selectedId === id ? {} : { role: id }, { replace: true });
  };

  const focus = hovered ?? scrollFocus;

  return (
    <div className="page">
      <h2 className="section-title">My professional experience...</h2>
      <p className="timeline__hint">Scroll to go back in time, and select a role to see what I worked on.</p>
      <ol className="timeline">
        {experience.map((role, i) => {
          const d = Math.abs(i - focus);
          const style = { '--s': scaleAt(d), '--o': opacityAt(d) } as React.CSSProperties;
          const isSelected = role.id === selectedId;
          return (
            <li key={role.id} className={`timeline__item${isSelected ? ' is-selected' : ''}`}>
              <div
                className="timeline__row"
                ref={(el) => (rowRefs.current[i] = el)}
                style={style}
                onMouseEnter={() => canHover() && setHovered(i)}
                onMouseLeave={() => setHovered(null)}
              >
                <button
                  type="button"
                  className="timeline__node"
                  aria-expanded={isSelected}
                  aria-controls={`role-${role.id}`}
                  onClick={() => toggle(role.id, i)}
                >
                  <span className="timeline__dot" />
                  <span className="timeline__text">
                    <span className="timeline__dates">{role.dates}</span>
                    <span className="timeline__title">{role.title}</span>
                    <span className="timeline__company">{role.company}</span>
                  </span>
                </button>
              </div>

              {isSelected && (
                <section id={`role-${role.id}`} className="role timeline__details">
                  <div className="role__header">
                    <h2>
                      {role.title} at {role.company}
                    </h2>
                    <p>{role.dates}</p>
                  </div>
                  <div className="role__body">
                    <h3>Summary</h3>
                    <p>{role.summary}</p>
                    {role.projects.length > 0 && <h3>Notable Projects</h3>}
                    {role.projects.map((project) => (
                      <details key={project.title} className="project">
                        <summary>{project.title}</summary>
                        <div className="project__body">
                          <h4>Tools Used</h4>
                          <p>{project.tools.join(', ')}</p>
                          <details className="project__section">
                            <summary>Description</summary>
                            <p>{project.description}</p>
                          </details>
                          <details className="project__section">
                            <summary>Impact</summary>
                            <p>{project.impact}</p>
                          </details>
                        </div>
                      </details>
                    ))}
                  </div>
                </section>
              )}
            </li>
          );
        })}
      </ol>

      <div className="intro">
        <section className="intro__section">
          <h2 className="section-title">A little about me...</h2>
          <div className="box about">
            {profile.about.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </div>
        </section>

        <section className="intro__section">
          <h2 className="section-title">Contact</h2>
          <div className="box contact">
            <p className="contact__lead">I am always open to hearing about new opportunities!</p>
            <ul className="contact__list">
              {contacts.map(({ label, value, href, Icon }) => (
                <li key={label}>
                  <a
                    className="contact__link"
                    href={href}
                    {...(href.startsWith('mailto:') ? {} : { target: '_blank', rel: 'noreferrer' })}
                  >
                    <span className="contact__icon">
                      <Icon />
                    </span>
                    <span>
                      <span className="contact__label">{label}</span>
                      <span className="contact__value">{value}</span>
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
}

export default Professional;
